import { useEffect, useMemo, useState } from "react";
import { getSystemOptionVariantsCount, getSystemTotalFromUnit, getSystemUnitFromTotal } from "src/rules/calculation/systemHelpers";
import { getEffectiveBankerCount, getEffectiveSelectionsCount } from "src/common/helpers/betBuilder";
import { useRecoilState, useRecoilValue } from "recoil";
import StakeInput from "src/common/components/stakeInput";
import { decimalString } from "src/constants/types";

import { BetTypeEnum } from "src/constants/enums/betType";
import selectionsStore from "src/stores/selections";
import upperDataStore from "src/stores/upperData";
import { usePrevious } from "src/hooks/previous";
import mainStore from "src/stores/main";

import './style.scss';

interface IProps {
  totalStake: decimalString;
  onChange(value: decimalString): void;
}

const SystemStakeInput = ({ totalStake, onChange }: IProps) => {
  const [{ systemCombinationCount, systemUnitStake }, setMainState] = useRecoilState(mainStore);
  const { selections } = useRecoilValue(selectionsStore);
  const { currencyConfig } = useRecoilValue(upperDataStore);

  // BB cards collapse to ONE leg each for combination math.
  const effectiveCount = useMemo(() => getEffectiveSelectionsCount(selections), [selections]);
  const bankersCount = useMemo(() => getEffectiveBankerCount(selections), [selections]);

  const optionVariantsCount = getSystemOptionVariantsCount(
    systemCombinationCount - bankersCount,
    effectiveCount - bankersCount,
  );

  const prevOptionVariantsCount = usePrevious(optionVariantsCount);

  useEffect(() => {
    if (optionVariantsCount !== prevOptionVariantsCount) {
      setMainState(oldMainState => ({
        ...oldMainState,
        systemUnitStake: getSystemUnitFromTotal(totalStake, optionVariantsCount, currencyConfig)
      }));
    }
  }, [totalStake, optionVariantsCount, prevOptionVariantsCount, currencyConfig, setMainState]);

  useEffect(() => {
    return () => {
      setMainState(oldMainState => ({ ...oldMainState, systemUnitStake: undefined }));
    }
  }, [setMainState]);

  const changeUnitStake = (stake: decimalString) => {
    onChange(getSystemTotalFromUnit(stake, optionVariantsCount, currencyConfig));
    setMainState(oldMainState => ({ ...oldMainState, systemUnitStake: stake }));
  };

  const changeTotalStake = (stake: decimalString) => {
    onChange(stake)
    setMainState(oldMainState => ({
      ...oldMainState,
      systemUnitStake: getSystemUnitFromTotal(stake, optionVariantsCount, currencyConfig)
    }));
  };

  return <>
    <StakeInput
      value={systemUnitStake || ''}
      onChange={changeUnitStake}
      className="B-system-stake-input"
      placeholder="Unit Stake"
    />

    <StakeInput
      value={totalStake}
      onChange={changeTotalStake}
      className="B-system-total-stake-input"
      placeholder="Total stake"
      showMaxBetButton
      betType={BetTypeEnum.System}
    />
  </>;
};

export default SystemStakeInput;
